import React, { useMemo } from 'react';

import MarkdownRenderer from '../MarkdownRenderer';
import { sanitizeHtml } from '../../utils/sanitize';
import {
  formatValue,
  normalizePlan,
  resolveFields,
  resolvePath,
  resolveRowUrl,
  resolveRows,
  surface,
} from './renderPlan';

/**
 * Renders a single record from a backend render plan.
 *
 * This is the counterpart to DynamicTable for tools that return one object rather
 * than a list — fetching one issue, one email, one order. Previously the plan
 * schema had no shape for those, so single-record tools produced no component
 * at all and the model filled the gap by typing a markdown table, differently
 * every time.
 *
 * Sharing renderPlan with DynamicTable is deliberate: field resolution, type
 * formatting and the dark surface tokens stay identical, so a record looks the
 * same whether you searched for it or fetched it directly.
 */
const DynamicDetail = ({ plan: rawPlan, data, uiView }) => {
  const plan = useMemo(() => normalizePlan(rawPlan), [rawPlan]);
  const rows = useMemo(() => resolveRows(plan, data), [plan, data]);
  const record = rows[0];
  const fields = useMemo(
    () => resolveFields(plan, uiView, rows),
    [plan, uiView, rows],
  );

  if (!plan || !record || !fields.length) return null;

  const url = resolveRowUrl(plan, record);

  return (
    <div
      style={{
        width: '100%',
        marginBottom: '1rem',
        borderRadius: '0.5rem',
        border: `1px solid ${surface.border}`,
        backgroundColor: surface.bg,
        overflow: 'hidden',
      }}
    >
      <dl style={{ margin: 0, fontSize: '0.75rem', color: surface.text }}>
        {fields.map((f, i) => {
          const text = formatValue(resolvePath(record, f.value_path), f);
          // Rich content (a Jira/Confluence-style body) needs the same block
          // layout as plain long text — false here would jam a rendered
          // heading or table onto the same line as its label.
          const isRich = f.format === 'markdown' || f.format === 'html';
          const isLong =
            isRich || (typeof text === 'string' && text.length > 120);
          return (
            <div
              key={f.id}
              style={{
                display: 'flex',
                flexDirection: isLong ? 'column' : 'row',
                gap: isLong ? '0.25rem' : '1rem',
                padding: '0.5rem 1rem',
                borderTop: i === 0 ? 'none' : `1px solid ${surface.rowBorder}`,
              }}
            >
              <dt
                style={{
                  flex: isLong ? undefined : '0 0 30%',
                  maxWidth: isLong ? undefined : '11rem',
                  color: surface.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.6875rem',
                }}
              >
                {f.label}
              </dt>
              {text === null ? (
                <dd
                  style={{
                    margin: 0,
                    flex: 1,
                    minWidth: 0,
                    color: surface.dim,
                  }}
                >
                  —
                </dd>
              ) : f.format === 'markdown' ? (
                <dd style={{ margin: 0, flex: 1, minWidth: 0 }}>
                  <MarkdownRenderer markdown={text} />
                </dd>
              ) : f.format === 'html' ? (
                // Scoped to this field's own div, not the full sanitizeHtml +
                // iframe treatment ToolApprovalUI uses for email bodies — email
                // HTML is adversarial (arbitrary senders, layout-breaking CSS)
                // and needs that isolation; a Confluence/Jira body from the
                // user's own connected workspace is lower-risk and reads more
                // naturally inline with the rest of the record.
                <dd
                  style={{
                    margin: 0,
                    flex: 1,
                    minWidth: 0,
                    wordBreak: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
                />
              ) : (
                <dd
                  style={{
                    margin: 0,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: isLong ? 'pre-wrap' : 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {text}
                </dd>
              )}
            </div>
          );
        })}
      </dl>

      {url && (
        <div
          style={{
            borderTop: `1px solid ${surface.rowBorder}`,
            padding: '0.5rem 1rem',
          }}
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#818cf8', fontSize: '0.75rem' }}
          >
            Open original →
          </a>
        </div>
      )}
    </div>
  );
};

export default DynamicDetail;
